import React, { useState } from 'react';
import { 
  FileText, 
  Calendar as CalendarIcon,
  User,
  MapPin,
  Briefcase,
  Loader2,
  Download
} from 'lucide-react';
import { generateOfferLetterPDF } from '../../utils/pdfOfferLetter';
import toast from 'react-hot-toast';

export const OfferLetterCreatePage: React.FC = () => {
  const [date, setDate] = useState<string>(() => {
    // Default to today's date
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [internName, setInternName] = useState<string>('');
  const [internAddressLine1, setInternAddressLine1] = useState<string>('');
  const [internCityStatePin, setInternCityStatePin] = useState<string>('');
  const [hrManagerName, setHrManagerName] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDate(e.target.value);
  };

  const formatDateFromInput = (dateInput: string): string => {
    if (!dateInput) return '';
    try {
      const d = new Date(dateInput);
      return d.toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateInput;
    }
  };

  const handleCreateOfferLetter = async () => {
    // Validation
    if (!date.trim()) {
      toast.error('Please select a date');
      return;
    }
    if (!internName.trim()) {
      toast.error('Please enter intern name');
      return;
    }
    if (!internAddressLine1.trim()) {
      toast.error('Please enter intern address');
      return;
    }
    if (!internCityStatePin.trim()) {
      toast.error('Please enter city, state and PIN');
      return;
    }
    if (!hrManagerName.trim()) {
      toast.error('Please enter HR manager name');
      return;
    }

    setIsGenerating(true);
    
    try {
      const formattedDate = formatDateFromInput(date);
      
      await generateOfferLetterPDF({
        date: formattedDate,
        internName: internName.trim(),
        internAddressLine1: internAddressLine1.trim(),
        internCityStatePin: internCityStatePin.trim(),
        hrManagerName: hrManagerName.trim()
      });
      
      toast.success('Offer letter PDF downloaded successfully!');
      
      // Reset form (but keep date as today)
      setInternName('');
      setInternAddressLine1('');
      setInternCityStatePin('');
      setHrManagerName('');
      const today = new Date();
      setDate(today.toISOString().split('T')[0]);
    } catch (error: any) {
      console.error('Error generating offer letter PDF:', error);
      toast.error(error.message || 'Failed to generate offer letter PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <FileText className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Create Offer Letter</h1>
              <p className="text-sm text-gray-500">Trip Captain Intern — Offer Letter Generator</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={(e) => { e.preventDefault(); handleCreateOfferLetter(); }}>
              <div className="space-y-6">
                {/* Date */}
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      id="date"
                      value={date}
                      onChange={handleDateChange}
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Intern Name */}
                <div>
                  <label htmlFor="internName" className="block text-sm font-medium text-gray-700 mb-2">
                    Intern Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="internName"
                      value={internName}
                      onChange={(e) => setInternName(e.target.value)}
                      required
                      placeholder="Enter intern name"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Intern Address Line 1 */}
                <div>
                  <label htmlFor="internAddressLine1" className="block text-sm font-medium text-gray-700 mb-2">
                    Intern Address Line 1 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <textarea
                      id="internAddressLine1"
                      value={internAddressLine1}
                      onChange={(e) => setInternAddressLine1(e.target.value)}
                      required
                      rows={2}
                      placeholder="Enter address line 1"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                    />
                  </div>
                </div>

                {/* City, State — PIN */}
                <div>
                  <label htmlFor="internCityStatePin" className="block text-sm font-medium text-gray-700 mb-2">
                    City, State — PIN <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="internCityStatePin"
                      value={internCityStatePin}
                      onChange={(e) => setInternCityStatePin(e.target.value)}
                      required
                      placeholder="e.g., Bengaluru, Karnataka — 560102"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* HR Manager Name */}
                <div>
                  <label htmlFor="hrManagerName" className="block text-sm font-medium text-gray-700 mb-2">
                    HR Manager Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="hrManagerName"
                      value={hrManagerName}
                      onChange={(e) => setHrManagerName(e.target.value)}
                      required
                      placeholder="Enter HR manager name"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isGenerating}
                    className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-5 w-5" />
                        <span>Create Offer Letter</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

